import { FormEvent, useEffect, useRef, useState } from 'react'
import { ArrowRight, Check, AlertCircle, ChevronDown, Download, Loader2, ShieldCheck, X } from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'
import { captureAttribution, getGtagAttributionParams } from '@/lib/attribution'
import { submitBackendLead } from '@/lib/submitToBackend'
import { toLogicalPath } from '@/i18n/locale'
import { cn } from '@/lib/utils'
import {
  fwConsentClass,
  fwErrorClass,
  fwInputClass,
  fwInputErrorClass,
  fwSubmitClass,
} from '@/lib/formStyles'
import { COUNTRIES, DEFAULT_COUNTRY, isValidPhone, normalizePhone, type Country } from '@/lib/phone'
import coverSrc from '../../docs/cursor-handoff/morocco-local-seo/generated-assets/whatsapp-playbook-clinic-conversations-cover.webp'

const DISMISSED_KEY = 'fw_guide_popup_dismissed_v2'
const CLAIMED_KEY = 'fw_guide_popup_claimed_v2'
const CLAIM_TTL = 90 * 24 * 60 * 60 * 1000

type AnalyticsWindow = Window & {
  gtag?: (...args: unknown[]) => void
  fbq?: (...args: unknown[]) => void
  __activeMetaPixelId?: string
}

type Field = 'name' | 'phone'
const messages: Record<Field, string> = {
  name: 'Indiquez votre nom complet.',
  phone: 'Indiquez un numéro WhatsApp valide.',
}

const benefits = [
  'Répondre aux demandes de prix sans perdre le patient',
  'Passer d’Instagram ou Facebook à WhatsApp',
  'Proposer le rendez-vous et relancer sans insister',
]

const POPUP_PATHS = new Set([
  '/dentaire',
  '/morocco-doctors',
  '/doctors-agent',
  '/doctors-agent-2',
  '/case-studies/clinique-dentaire-casablanca-agent-ia-whatsapp',
  '/projects/landing-page-dentaire-automatisation-whatsapp',
])

export function FlagImg({ code }: { code: string }) {
  return (
    <img
      src={`https://flagcdn.com/w40/${code}.png`}
      srcSet={`https://flagcdn.com/w80/${code}.png 2x`}
      alt=""
      width={20}
      height={14}
      className="block h-[14px] w-5 rounded-[2px] object-cover"
      loading="lazy"
    />
  )
}

function isEligiblePage(path: string) {
  const logical = toLogicalPath(path)
  if (logical.includes('/merci')) return false
  return POPUP_PATHS.has(logical)
}

export function GuideLeadPopup() {
  const { pathname, search } = useLocation()
  const eligible = isEligiblePage(pathname)
  const forceOpen = new URLSearchParams(search).get('lead') === 'open'
  const [open, setOpen] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [country, setCountry] = useState<Country>(DEFAULT_COUNTRY)
  const [showPicker, setShowPicker] = useState(false)
  const [phoneFocused, setPhoneFocused] = useState(false)
  const [isWhatsApp, setIsWhatsApp] = useState(true)
  const [touched, setTouched] = useState<Record<Field, boolean>>({ name: false, phone: false })
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({})
  const [formError, setFormError] = useState('')
  const triggered = useRef(false)
  const dialog = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const countryRef = useRef<HTMLDivElement>(null)
  const [viewport, setViewport] = useState<{ height: number; offsetTop: number } | null>(null)

  const dismiss = () => {
    if (!forceOpen) {
      try { sessionStorage.setItem(DISMISSED_KEY, '1') } catch { /* Storage may be disabled. */ }
    }
    setOpen(false)
  }

  useEffect(() => {
    triggered.current = false
    setOpen(false)
    setSubmitted(false)
    setShowPicker(false)
    if (!eligible) return

    const show = () => {
      if (triggered.current) return
      triggered.current = true
      captureAttribution()
      setOpen(true)
    }

    if (forceOpen) {
      show()
      return
    }

    try {
      const claimedAt = Number(localStorage.getItem(CLAIMED_KEY))
      if (sessionStorage.getItem(DISMISSED_KEY) || (claimedAt && Date.now() - claimedAt < CLAIM_TTL)) return
    } catch { /* Show the popup when storage is unavailable. */ }

    const timer = window.setTimeout(show, 8000)
    const onScroll = () => {
      const distance = document.documentElement.scrollHeight - window.innerHeight
      if (distance > 0 && window.scrollY / distance >= 0.42) show()
    }
    const onExit = (event: MouseEvent) => {
      if (event.clientY > 8) return
      show()
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    document.documentElement.addEventListener('mouseleave', onExit)
    return () => {
      clearTimeout(timer)
      window.removeEventListener('scroll', onScroll)
      document.documentElement.removeEventListener('mouseleave', onExit)
    }
  }, [eligible, forceOpen, pathname, search])

  useEffect(() => {
    if (!open) return
    const oldOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const focusTimer = window.setTimeout(() => dialog.current?.focus(), 0)
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') dismiss() }
    const syncViewport = () => {
      const vv = window.visualViewport
      setViewport({
        height: vv?.height ?? window.innerHeight,
        offsetTop: vv?.offsetTop ?? 0,
      })
    }
    syncViewport()
    window.visualViewport?.addEventListener('resize', syncViewport)
    window.visualViewport?.addEventListener('scroll', syncViewport)
    window.addEventListener('resize', syncViewport)
    document.addEventListener('keydown', onKey)
    return () => {
      clearTimeout(focusTimer)
      document.body.style.overflow = oldOverflow
      window.visualViewport?.removeEventListener('resize', syncViewport)
      window.visualViewport?.removeEventListener('scroll', syncViewport)
      window.removeEventListener('resize', syncViewport)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  function validate(field: Field, value: string) {
    const ok = field === 'name' ? value.trim().length >= 2 : isValidPhone(value, country.dial)
    setErrors((current) => {
      const next = { ...current }
      if (ok) delete next[field]
      else next[field] = messages[field]
      return next
    })
    return ok
  }

  useEffect(() => {
    if (!showPicker) return
    const onPointer = (event: MouseEvent) => {
      if (!countryRef.current?.contains(event.target as Node)) setShowPicker(false)
    }
    document.addEventListener('mousedown', onPointer)
    return () => document.removeEventListener('mousedown', onPointer)
  }, [showPicker])

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setFormError('')
    setTouched({ name: true, phone: true })
    const nameOk = validate('name', name)
    const phoneOk = validate('phone', phone)
    if (!nameOk || !phoneOk) return
    const normalizedPhone = normalizePhone(phone, country.dial)
    setSubmitting(true)
    try {
      await submitBackendLead({
        landingSource: `guide-popup:${pathname}`,
        name: name.trim(),
        phone: normalizedPhone,
        notes: `[LEAD MAGNET - GUIDE CONVERSION MESSAGES]\nEnvoyer le guide gratuit sur WhatsApp.\nPage: ${pathname}\nWhatsApp: ${isWhatsApp ? 'OUI' : 'NON'}\nPays: ${country.name} (${country.dial})`,
      })
      try {
        localStorage.setItem(CLAIMED_KEY, String(Date.now()))
        sessionStorage.setItem(DISMISSED_KEY, '1')
      } catch { /* Conversion is already saved. */ }
      const analytics = window as AnalyticsWindow
      analytics.gtag?.('event', 'lead_submit', { event_category: 'Lead Magnet', event_label: 'Guide Conversion Messages', ...getGtagAttributionParams() })
      if (analytics.fbq && analytics.__activeMetaPixelId) {
        analytics.fbq('trackSingle', analytics.__activeMetaPixelId, 'Lead', {
          content_name: 'Guide Conversion Messages',
        })
      }
      setSubmitted(true)
    } catch {
      setFormError("Nous n'avons pas pu enregistrer votre demande. Réessayez dans un instant.")
    } finally { setSubmitting(false) }
  }

  if (!eligible || !open) return null

  const nameInvalid = Boolean(errors.name && touched.name)
  const phoneInvalid = Boolean(errors.phone && touched.phone)

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[10000] flex items-end justify-center bg-fw-ink/70 font-fw overscroll-none sm:items-center sm:p-6"
      style={viewport ? { top: viewport.offsetTop, height: viewport.height } : { top: 0 }}
      onMouseDown={(event) => event.target === event.currentTarget && dismiss()}
    >
      <div
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="guide-popup-title"
        tabIndex={-1}
        className="relative flex w-full max-w-[960px] flex-col overflow-hidden rounded-t-[24px] border border-fw-hairline bg-fw-white shadow-fw-panel outline-none max-h-[92%] sm:max-h-[min(640px,90dvh)] sm:rounded-[24px]"
      >
        <button
          ref={closeRef}
          type="button"
          onClick={dismiss}
          aria-label="Fermer"
          className="absolute right-3 top-3 z-20 grid h-11 w-11 place-items-center rounded-full border border-fw-hairline bg-fw-pure text-fw-graphite transition hover:border-fw-violet-border hover:text-fw-ink sm:right-4 sm:top-4"
        >
          <X size={18} strokeWidth={2.2} />
        </button>

        {submitted ? (
          <div className="flex min-h-[min(420px,70dvh)] flex-col items-center justify-center overflow-y-auto px-5 py-14 text-center sm:px-10">
            <span className="mb-6 grid h-16 w-16 place-items-center rounded-full border border-fw-lavender-border bg-fw-lavender text-fw-violet">
              <Download size={24} strokeWidth={2} aria-hidden="true" />
            </span>
            <p className="flex items-center gap-2.5 font-fwmono text-[11px] font-medium uppercase tracking-[0.16em] text-fw-violet">
              <span className="h-px w-[22px] bg-fw-violet" aria-hidden />
              Demande reçue
            </p>
            <h2 id="guide-popup-title" className="mt-4 max-w-xl text-[1.65rem] font-bold tracking-tight text-fw-ink sm:text-3xl">
              Votre guide arrive sur WhatsApp
            </h2>
            <p className="mt-4 max-w-lg text-base leading-7 text-fw-graphite">
              Merci {name.trim().split(' ')[0]}. Vous allez le recevoir sur WhatsApp dans un instant, pour le partager avec votre assistante.
            </p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className={cn(fwSubmitClass, 'mt-8 max-w-xs')}
            >
              Continuer la visite
            </button>
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:grid lg:h-full lg:min-h-[560px] lg:grid-cols-[.9fr_1.1fr]">
            <div className="relative order-first h-[100px] shrink-0 overflow-hidden bg-fw-mist sm:h-[120px] lg:order-none lg:col-start-1 lg:row-start-1 lg:h-auto lg:min-h-full">
              <div className="pointer-events-none absolute -right-8 -top-8 size-[240px] rounded-full bg-fw-violet opacity-40 blur-3xl" aria-hidden="true" />
              <figure className="relative h-full w-full">
                <img
                  src={coverSrc}
                  alt="Couverture du playbook WhatsApp pour conversations de clinique"
                  width="1200"
                  height="675"
                  className="h-full w-full object-cover object-center"
                />
                <figcaption className="absolute bottom-4 left-4 hidden items-center gap-1.5 rounded-lg border border-fw-lavender-border bg-fw-lavender px-2.5 py-1.5 text-[13px] font-medium text-fw-violet lg:inline-flex">
                  <Check size={13} strokeWidth={2.2} aria-hidden="true" />
                  Prêt à utiliser
                </figcaption>
              </figure>
            </div>

            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain px-5 pt-4 pb-4 sm:px-8 sm:py-6 lg:col-start-2 lg:row-start-1 lg:overflow-hidden lg:px-8 lg:py-7">
              <p className="flex items-center gap-2.5 font-fwmono text-[11px] font-medium uppercase tracking-[0.16em] text-fw-violet">
                <span className="h-px w-[22px] bg-fw-violet" aria-hidden />
                Guide gratuit pour cabinets
              </p>
              <p className="mt-2.5 hidden text-lg font-semibold tracking-tight text-fw-ink lg:block">Vous partez déjà ?</p>
              <h2 id="guide-popup-title" className="mt-1 text-[1.35rem] font-bold leading-[1.15] tracking-tight text-fw-ink lg:text-[1.6rem]">
                Emportez les scripts de conversion.
              </h2>
              <p className="mt-2 text-base leading-6 text-fw-graphite">
                Recevez <strong className="font-semibold text-fw-ink">Du message au rendez-vous</strong>, le playbook WhatsApp utilisé avec de grandes cliniques au Maroc.
              </p>
              <ul className="mt-3 grid gap-1.5 text-base leading-snug text-fw-ink">
                {benefits.map((item) => (
                  <li key={item} className="flex items-start gap-2.5">
                    <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-fw-lavender text-fw-violet">
                      <Check size={13} strokeWidth={2.2} aria-hidden="true" />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>

              <form id="guide-popup-form" onSubmit={submit} className="mt-4 flex flex-1 flex-col gap-2.5" noValidate>
                <div className="grid gap-2.5 sm:grid-cols-2">
                  <label className="flex flex-col gap-1">
                    <span className={cn('text-[13px] font-medium', nameInvalid ? 'text-fw-error' : 'text-fw-ink')}>
                      Nom complet <span className="text-fw-violet">*</span>
                    </span>
                    <input
                      value={name}
                      onChange={(event) => setName(event.target.value)}
                      onBlur={() => { setTouched((current) => ({ ...current, name: true })); validate('name', name) }}
                      autoComplete="name"
                      placeholder="Votre nom complet"
                      className={cn(fwInputClass, nameInvalid && fwInputErrorClass)}
                    />
                  </label>
                  <div className="flex flex-col gap-1">
                    <span className={cn('text-[13px] font-medium', phoneInvalid ? 'text-fw-error' : 'text-fw-ink')}>
                      WhatsApp <span className="text-fw-violet">*</span>
                    </span>
                    <div ref={countryRef} className="relative">
                      <div
                        className={cn(
                          'flex h-[50px] items-center overflow-hidden rounded-xl border bg-fw-pure transition-[border-color,box-shadow] duration-150',
                          phoneInvalid
                            ? 'border-fw-error'
                            : phoneFocused
                              ? 'border-fw-violet shadow-fw-focus'
                              : 'border-fw-hairline',
                        )}
                      >
                        <button
                          type="button"
                          onClick={() => setShowPicker((current) => !current)}
                          aria-label="Choisir le pays"
                          className="flex h-full shrink-0 items-center gap-1.5 bg-transparent px-3.5 pl-3.5 pr-2.5"
                        >
                          <FlagImg code={country.code} />
                          <ChevronDown size={11} className="text-fw-muted" strokeWidth={2.2} />
                        </button>
                        <span className="h-6 w-px shrink-0 bg-fw-hairline" aria-hidden="true" />
                        <span className="shrink-0 select-none px-2.5 pl-2.5 text-[13px] text-fw-graphite">
                          {country.dial}
                        </span>
                        <input
                          value={phone}
                          onChange={(event) => {
                            setPhone(event.target.value)
                            if (event.target.value) validate('phone', event.target.value)
                            else setErrors((current) => {
                              const next = { ...current }
                              delete next.phone
                              return next
                            })
                          }}
                          onFocus={() => setPhoneFocused(true)}
                          onBlur={() => {
                            setPhoneFocused(false)
                            setTouched((current) => ({ ...current, phone: true }))
                            if (phone) validate('phone', phone)
                          }}
                          type="tel"
                          inputMode="tel"
                          autoComplete="tel"
                          placeholder="06 00 00 00 00"
                          className="min-w-0 flex-1 bg-transparent py-0 pr-3.5 text-[15px] font-semibold tracking-[0.02em] text-fw-ink outline-none placeholder:font-normal placeholder:tracking-normal placeholder:text-fw-muted"
                        />
                      </div>

                      {showPicker ? (
                        <div className="absolute left-0 top-[calc(100%+4px)] z-30 max-h-[210px] w-full overflow-y-auto rounded-xl border border-fw-hairline bg-fw-white shadow-fw-panel">
                          {COUNTRIES.map((item) => (
                            <button
                              key={item.code}
                              type="button"
                              onClick={() => {
                                setCountry(item)
                                setShowPicker(false)
                                if (phone) validate('phone', phone)
                              }}
                              className={cn(
                                'flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-[13px]',
                                country.code === item.code
                                  ? 'bg-fw-lavender font-semibold text-fw-ink'
                                  : 'bg-transparent font-normal text-fw-ink hover:bg-fw-mist-soft',
                              )}
                            >
                              <FlagImg code={item.code} />
                              <span className="flex-1">{item.name}</span>
                              <span className="text-[11px] text-fw-muted">{item.dial}</span>
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
                <label className="flex cursor-pointer select-none items-center gap-2">
                  <input
                    type="checkbox"
                    checked={isWhatsApp}
                    onChange={(event) => setIsWhatsApp(event.target.checked)}
                    className="size-[15px] accent-emerald-500"
                  />
                  <span className="text-xs text-fw-muted">Ce numéro est sur WhatsApp</span>
                </label>
                {nameInvalid ? (
                  <p role="alert" className={fwErrorClass}>
                    <AlertCircle size={13} strokeWidth={2.2} />
                    {errors.name}
                  </p>
                ) : null}
                {phoneInvalid ? (
                  <p role="alert" className={fwErrorClass}>
                    <AlertCircle size={13} strokeWidth={2.2} />
                    {errors.phone}
                  </p>
                ) : null}
                {formError ? (
                  <p role="alert" className={fwErrorClass}>
                    <AlertCircle size={13} strokeWidth={2.2} />
                    {formError}
                  </p>
                ) : null}
                <p className={cn(fwConsentClass, 'flex items-start gap-2')}>
                  <ShieldCheck size={15} strokeWidth={2} className="mt-0.5 shrink-0 text-fw-violet" />
                  <span>
                    Vos coordonnées restent confidentielles et ne sont jamais revendues.{' '}
                    <Link to="/privacy" className="font-medium text-fw-ink">Confidentialité</Link>
                  </span>
                </p>
                <button type="submit" disabled={submitting} className={cn(fwSubmitClass, 'mt-auto hidden shrink-0 lg:inline-flex')}>
                  {submitting ? (
                    <><Loader2 size={17} className="animate-spin" /> Envoi en cours…</>
                  ) : (
                    <>Recevoir le guide sur WhatsApp <ArrowRight size={17} /></>
                  )}
                </button>
              </form>
            </div>

            <div className="shrink-0 border-t border-fw-hairline bg-fw-white px-5 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] lg:hidden">
              <button type="submit" form="guide-popup-form" disabled={submitting} className={fwSubmitClass}>
                {submitting ? (
                  <><Loader2 size={17} className="animate-spin" /> Envoi en cours…</>
                ) : (
                  <>Recevoir le guide sur WhatsApp <ArrowRight size={17} /></>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
